import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LuCalendarDays } from 'react-icons/lu';
import { Row } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { BrowserView, isMobile, MobileView } from 'react-device-detect';
import DATE_FORMATS from '@constants/dateFormat';
import { format } from 'date-fns';
import { groupBy } from 'lodash';
import { MemberAddHoursModal } from '@company/pages/JobOverview/ActivityTracker/MemberAddHoursModal/MemberAddHoursModal';
import CreateShiftMobileModal from '@company/pages/JobOverview/Scheduling/CreateShift/CreateShiftMobileModal';
import ShiftReviewModal from '@company/pages/JobOverview/ActivityTracker/ShiftReviewModal/ShiftReviewModal';
import CreateShiftModal from '@company/pages/JobOverview/Scheduling/CreateShift/CreateShiftModal';
import JobShiftsTableItem from '@company/components/JobList/JobShifts/JobShiftsTableItem';
import { UserActionAssociatedTypeEnum } from '@company/types/comm-hub.type';
import { JOB_SHIFTS_PER_PAGE } from '@company/hooks/useJobListQueries';
import { useChatContext } from '@company/state/chatContext';
import { ActivityDetail } from '@th-types/daily-activity.type';
import { AddHours } from '@th-types/time-sheet.type';
import IJobDetail, {
  JobMember,
  JobAssignment,
  ShiftPosition,
} from '@th-types/job.detail.type';
import {
  IJobShiftsData,
  IJobShiftsResult,
  IJobStats,
} from '@th-types/jobs.type';
import ThPagination from '@components/ThPagination/ThPagination';
import JobMessageChat from '@components/Chat/JobMessageChat';
import SideEndModal from '@components/elements/SideEndModal';
import {
  handleRemoveFromListOnlyOptimisticUpdate,
  invalidateQueriesForDismissedItems,
} from '@utils/ComHubUtils';
import { WorkerModal } from '@company/components/WorkerModal';
import JobShiftsCardMobile from './JobShifts/JobShiftsMobile/JobShiftsCardMobile';

interface Props {
  jobStatsList: IJobStats[] | undefined;
  jobShiftsResult: IJobShiftsResult | undefined;
  queryJobShiftsList: string[];
  showJobMembers: (members: JobMember[]) => void;
  setCurrentOffset: (currentOffset: number) => void;
  handleOpenModalShareJobLink: (job: IJobDetail) => void;
  handleConfirmMarkPaid: (activityDetail: ActivityDetail) => void;
  completeOnboarding: (jobAssignment: JobAssignment, complete: boolean) => void;
  showAlertMessage: (alertMessage: string, success: boolean) => void;
  approveTimeSheet: (activityDetail: ActivityDetail) => void;
  onMarkPaidSuccess: (activityDetail: ActivityDetail) => void;
  onCancelShiftSuccess: (activityDetail: ActivityDetail) => void;
  submitHoursSuccess: (activity: ActivityDetail) => void;
  handleMissingHourNoticed: (shiftPositionId: number) => void;
  handleMissingActivityNoticed: (shiftPositionId: number) => void;
}

export default function JobShiftsView({
  jobStatsList,
  jobShiftsResult,
  queryJobShiftsList,
  showJobMembers,
  showAlertMessage,
  setCurrentOffset,
  completeOnboarding,
  handleConfirmMarkPaid,
  approveTimeSheet,
  onMarkPaidSuccess,
  onCancelShiftSuccess,
  submitHoursSuccess,
  handleMissingHourNoticed,
  handleOpenModalShareJobLink,
  handleMissingActivityNoticed,
}: Props) {
  const [showReviewShiftModal, setShowReviewShiftModal] = useState<boolean>();
  const [selectedActivity, setSelectedActivity] = useState<ActivityDetail>();
  const [showSubmitHoursModal, setShowSubmitHoursModal] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showWorkerProfile, setShowWorkerProfile] = useState(false);
  const [workerProfileId, setWorkerProfileId] = useState<number | undefined>();
  const [workerJobAssignmentId, setWorkerJobAssignmentId] = useState<
    number | undefined
  >();
  const [workerJobId, setWorkerJobId] = useState<number | undefined>();
  const [selectedShiftPosition, setSelectedShiftPosition] =
    useState<ShiftPosition>();
  const [showCreateShiftModal, setShowCreateShiftModal] = useState(false);
  const openCreateShiftModal = () => setShowCreateShiftModal(true);
  const closeCreateShiftModal = () => setShowCreateShiftModal(false);

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { chatPerson, clearChatData, showPrivateInfo, originalJobMessage } =
    useChatContext();

  const handleOpenReviewShiftModal = useCallback(
    (activity: ActivityDetail) => {
      setSelectedActivity(activity);
      setShowReviewShiftModal(true);
    },
    [setShowReviewShiftModal]
  );

  useEffect(() => {
    if (isChatOpen && !chatPerson && selectedActivity) {
      handleOpenReviewShiftModal(selectedActivity);
      setIsChatOpen(false);
    }
  }, [
    chatPerson,
    isChatOpen,
    selectedActivity,
    setIsChatOpen,
    handleOpenReviewShiftModal,
  ]);

  // Group activities by date
  const groupedByDate = Object.entries(
    groupBy(
      jobShiftsResult?.jobShifts?.filter(
        (item) => item?.jobShiftActivities && item.jobShiftActivities.length > 0
      ),
      'date'
    )
  ).map(([date, data]) => ({ date, data }));

  if (
    !jobShiftsResult?.jobShifts ||
    jobShiftsResult.jobShifts.length === 0 ||
    !jobStatsList ||
    jobStatsList.length === 0
  ) {
    return (
      <div
        className={`d-flex justify-content-center align-items-center ${isMobile ? 'ms-4 mt-4 me-4' : 'mt-3 flex-column'}`}
      >
        No results, please clear your quick filters or select a different one
      </div>
    );
  }

  const openWorkerSideModal = (
    workerId: number,
    jobAssignmentId?: number,
    jobId?: number
  ) => {
    if (!showWorkerProfile) {
      setWorkerProfileId(workerId);
      setWorkerJobAssignmentId(jobAssignmentId);
      setWorkerJobId(jobId);
      setShowWorkerProfile(true);
    }
  };

  const handleCloseWorkerProfile = () => {
    setShowWorkerProfile(false);
    setWorkerProfileId(undefined);
    setWorkerJobAssignmentId(undefined);
    setWorkerJobId(undefined);
  };

  const getJob = (jobId: number) => {
    return jobShiftsResult.jobs.find((job) => job.id === jobId);
  };

  const handleCloseReviewShiftModal = () => {
    setShowReviewShiftModal(false);
    setSelectedActivity(undefined);
  };

  const handleCloseJobChat = async () => {
    if (originalJobMessage) {
      const items = [
        {
          associatedType: UserActionAssociatedTypeEnum.JOB_CHAT,
          associatedIds: [originalJobMessage.chatId],
        },
      ];
      const searchUnread = searchParams.get('searchUnread') === 'true';
      if (searchUnread) {
        handleRemoveFromListOnlyOptimisticUpdate(queryClient, items);
      }
      invalidateQueriesForDismissedItems(queryClient, items);
    }
    clearChatData();
  };

  const approveTimeSheetAction = async (activityDetail: ActivityDetail) => {
    approveTimeSheet(activityDetail);
    setShowReviewShiftModal(false);
  };

  const openChatAction = () => {
    setShowReviewShiftModal(false);
    setIsChatOpen(true);
  };

  const handleOpenEditShiftModal = (shiftPosition: ShiftPosition) => {
    openCreateShiftModal();
    setSelectedShiftPosition(shiftPosition);
  };

  const successCloseUpdateShiftModal = async (message?: string) => {
    setSelectedShiftPosition(undefined);
    closeCreateShiftModal();
    if (message) {
      showAlertMessage(message, true);
    }
    queryClient.invalidateQueries({
      queryKey: queryJobShiftsList,
    });
  };

  const renderMobileCard = (jobShifts: IJobShiftsData) => {
    const job = getJob(jobShifts.jobId);
    if (!job) {
      return null;
    }
    return (
      <JobShiftsCardMobile
        job={job}
        key={`${jobShifts.jobId}-${jobShifts.date}`}
        activities={jobShifts.jobShiftActivities}
        jobStats={jobStatsList?.find(
          (stats) => stats.jobId === jobShifts.jobId
        )}
        handleConfirmMarkPaid={handleConfirmMarkPaid}
        handleOpenEditShiftModal={handleOpenEditShiftModal}
        completeOnboardingAction={completeOnboarding}
        handleMissingHourNoticed={handleMissingHourNoticed}
        handleMissingActivityNoticed={handleMissingActivityNoticed}
      />
    );
  };

  const handleOpenSubmitHoursModal = (activity: ActivityDetail) => {
    setShowSubmitHoursModal(true);
    setSelectedActivity(activity);
  };

  const handleAddHoursModalSubmitSuccess = (
    submittedHours: AddHours,
    message: string
  ) => {
    if (selectedActivity) {
      showAlertMessage(message, true);
      setShowSubmitHoursModal(false);
      submitHoursSuccess(selectedActivity);
      setSelectedActivity(undefined);
    }
  };

  const markPaidSuccessAction = (activity: ActivityDetail) => {
    onMarkPaidSuccess(activity);
    setSelectedActivity((previous) => {
      if (!previous || !previous.shiftPosition) {
        return previous;
      }
      return {
        ...previous,
        shiftPosition: {
          ...previous.shiftPosition,
          isPaid: !previous.shiftPosition.isPaid,
        },
      };
    });
  };

  const shiftReviewModalSubmitHoursSuccess = (submittedHours: AddHours) => {
    setSelectedActivity((prevActivity) => {
      if (!prevActivity || !prevActivity.timeSheet) {
        return prevActivity;
      }
      return {
        ...prevActivity,
        timeSheet: {
          ...prevActivity.timeSheet,
          localizedStart: `${format(
            new Date(submittedHours.start),
            DATE_FORMATS.DATE_FORMAT
          )} ${submittedHours.startTime}`,
          localizedEnd: `${format(
            new Date(submittedHours.end),
            DATE_FORMATS.DATE_FORMAT
          )} ${submittedHours.endTime}`,
        },
      };
    });
  };

  return (
    <div>
      {groupedByDate.map((element) => {
        return (
          <div key={`${element.date}`}>
            <Row className="mx-3 mt-4">
              <div>
                <LuCalendarDays size={22} className="mb-2 me-1" />
                <span className="fs-5">
                  <strong>{element.date}</strong>
                </span>
                <hr className="mt-0" />
              </div>
            </Row>
            {element.data.map((jobShifts) => (
              <React.Fragment key={`${jobShifts.jobId}-${jobShifts.date}`}>
                <MobileView>{renderMobileCard(jobShifts)}</MobileView>
                <BrowserView>
                  <JobShiftsTableItem
                    job={getJob(jobShifts.jobId)}
                    jobStats={jobStatsList?.find(
                      (stats) => stats.jobId === jobShifts.jobId
                    )}
                    showJobMembers={showJobMembers}
                    handleConfirmMarkPaid={handleConfirmMarkPaid}
                    completeOnboarding={completeOnboarding}
                    jobShiftActivities={jobShifts.jobShiftActivities}
                    openWorkerSideModal={openWorkerSideModal}
                    handleOpenModalShareJobLink={handleOpenModalShareJobLink}
                    handleOpenEditShiftModal={handleOpenEditShiftModal}
                    handleOpenReviewShiftModal={handleOpenReviewShiftModal}
                    handleOpenSubmitHoursModal={handleOpenSubmitHoursModal}
                    handleMissingActivityNoticed={handleMissingActivityNoticed}
                    handleMissingHourNoticed={handleMissingHourNoticed}
                  />
                </BrowserView>
              </React.Fragment>
            ))}
          </div>
        );
      })}
      <div
        className={`${isMobile ? 'mb-5 mt-0' : 'mt-auto'}`}
        data-testid="th-pagination"
      >
        <ThPagination
          currentOffset={jobShiftsResult?.pagination.offset}
          onPageChange={setCurrentOffset}
          itemsPerPage={JOB_SHIFTS_PER_PAGE}
          totalItems={jobShiftsResult.pagination.count}
          data-testid="th-pagination"
        />
      </div>
      {showWorkerProfile && workerProfileId && (
        <WorkerModal
          workerId={workerProfileId}
          jobAssignmentId={workerJobAssignmentId}
          jobId={workerJobId}
          show={showWorkerProfile && !!workerProfileId}
          onClose={handleCloseWorkerProfile}
        />
      )}
      {chatPerson && (
        <SideEndModal
          show={!!chatPerson}
          width={isMobile ? '90%' : '50%'}
          onHide={handleCloseJobChat}
        >
          <JobMessageChat
            worker={chatPerson}
            showWorkerInfo={showPrivateInfo}
            onClose={handleCloseJobChat}
          />
        </SideEndModal>
      )}

      <MobileView>
        {showCreateShiftModal && selectedShiftPosition && (
          <CreateShiftMobileModal
            job={getJob(selectedShiftPosition.jobId)!}
            shift={selectedShiftPosition}
            handleClose={closeCreateShiftModal}
            successCloseModal={successCloseUpdateShiftModal}
          />
        )}
      </MobileView>
      <BrowserView>
        {showCreateShiftModal && selectedShiftPosition && (
          <CreateShiftModal
            job={getJob(selectedShiftPosition.jobId)!}
            shift={selectedShiftPosition}
            handleClose={closeCreateShiftModal}
            successCloseModal={successCloseUpdateShiftModal}
          />
        )}
      </BrowserView>
      {showReviewShiftModal && selectedActivity && (
        <ShiftReviewModal
          openChatAction={openChatAction}
          activityDetail={selectedActivity}
          approveTimeSheet={approveTimeSheetAction}
          handleClose={handleCloseReviewShiftModal}
          submitHoursSuccess={shiftReviewModalSubmitHoursSuccess}
          onMarkPaidSuccess={markPaidSuccessAction}
          onCancelShiftSuccess={onCancelShiftSuccess}
        />
      )}
      {showSubmitHoursModal && selectedActivity && (
        <MemberAddHoursModal
          handleSubmitHoursSuccess={handleAddHoursModalSubmitSuccess}
          setShowSubmitHoursModal={setShowSubmitHoursModal}
          activityDetail={selectedActivity}
        />
      )}
    </div>
  );
}
